// ============================================================
// SecaoSolicitacoes.js — decisão sobre justificativa de falta
//
// Duas mesas na mesma tela, porque a decisão é a mesma e o que muda é o
// alcance de quem decide:
//
//   Ação                                     Técnico          Coordenação
//   Aprovar ou negar conforme as regras      Sim, no núcleo   Sim
//                                            e modalidade
//   Pedir complemento ou correção            Sim              Sim
//   Abrir exceção às regras ou aos prazos    Não              Sim
//   Julgar recurso e reverter decisão        Não              Sim, com motivo
//   Decidir sobre a própria ausência         Não              Sim
//
// A régua de verdade está no servidor, em decisoes.js. Aqui ela é repetida
// para não oferecer um botão que o servidor vai recusar — o que ensina a
// pessoa a desconfiar da tela.
//
// Aprovar cria automaticamente a dispensa que abona a falta (na Cloud
// Function). Não há cálculo aqui.
// ============================================================

import { useState, useEffect, useCallback } from "react";
import {
  carregarSolicitacoesPendentes, carregarSolicitacoesDoMes,
  resolverSolicitacao, reverterSolicitacao, solicitarComplemento,
  carregarDecisoesParaCiencia, darCienciaNaDecisao,
} from "../firebase";
import { normalizarModalidade } from "../calculo";
import { CINZA, CINZA_ESC, TEXTO, VERDE, PERIGO, PERIGO_CL, AZUL, BORDA, OK, OK_CL, RUIM, RUIM_CL } from "../tema";
import { Card, Btn, StatusBox, Carregando } from "../componentes";
import useIsMobile from "../useIsMobile";

// O que o botão de aprovar realmente faz muda conforme a origem, e o
// rótulo precisa dizer isso: aprovar uma justificativa abona a falta,
// aprovar um registro do portal cria a presença, e um aviso de local só
// registra o parecer — a presença dele já existe e não é alterada.
function rotuloAprovar(origem) {
  if (origem === "portal_presenca") return "Aprovar (confirma presença)";
  if (origem === "fora_do_local") return "✓ Considerar regular";
  if (origem === "fora_do_raio") return "✓ Autorizar presença";
  return "Aprovar (abona a falta)";
}

// O anexo é uma coisa num pedido de justificativa (atestado, declaração) e
// outra num registro de presença (a foto tirada na hora). O rótulo precisa
// dizer qual dos dois, senão o coordenador clica sem saber o que abre.
function ehFotoDeRegistro(origem) {
  return origem === "portal_presenca" || origem === "fora_do_local"
    || origem === "fora_do_raio";
}

// Origens que criam PRESENÇA, e não abono de falta. Mesma lista do
// servidor: dizer que alguém esteve onde não devia, ou liberar uma presença
// bloqueada por distância, é decisão institucional e fica com a coordenação.
const ORIGENS_DE_PRESENCA = [
  "portal_presenca", "fora_do_local", "fora_do_raio", "rosto_nao_confere",
];

// Até quando o técnico decide. Depois disso o mês virou relatório, e
// reabrir fechamento é exceção ao prazo. Mesmo número do servidor.
const DIAS_PARA_O_TECNICO_DECIDIR = 30;

function diasDesde(iso, hojeISO) {
  const ok = (d) => /^\d{4}-\d{2}-\d{2}$/.test(String(d || ""));
  if (!ok(iso) || !ok(hojeISO)) return null;
  const [a1, m1, d1] = iso.split("-").map(Number);
  const [a2, m2, d2] = hojeISO.split("-").map(Number);
  return Math.round((Date.UTC(a2, m2 - 1, d2) - Date.UTC(a1, m1 - 1, d1)) / 86400000);
}

// O que o técnico pode decidir, e por que não pode o resto. Devolve null
// quando pode, ou a frase que a tela mostra no lugar dos botões.
export function impedimentoDoTecnico({ solicitacao, uid, modalidade, hojeISO }) {
  const s = solicitacao || {};
  if (normalizarModalidade(s.modalidade) !== normalizarModalidade(modalidade)) {
    return "Outra modalidade.";
  }
  if (s.atletaUid && s.atletaUid === uid) {
    return "A sua própria ausência é decidida pela coordenação.";
  }
  // Quem pede não decide: o pedido de correção que o próprio técnico
  // lançou vai para a coordenação, e não para a fila dele.
  if (s.solicitanteUid && s.solicitanteUid === uid) {
    return "Você pediu esta correção. Quem decide é a coordenação.";
  }
  if (ORIGENS_DE_PRESENCA.includes(s.origem)) {
    return s.origem === "rosto_nao_confere"
      ? "Aviso de reconhecimento facial: quem decide é a coordenação."
      : "Pedido sobre registro de presença: quem decide é a coordenação.";
  }
  const dias = diasDesde(s.dataTreino, hojeISO);
  if (dias !== null && dias > DIAS_PARA_O_TECNICO_DECIDIR) {
    return `O treino foi há ${dias} dias e o período já fechou. Abrir exceção ao prazo é da coordenação.`;
  }
  return null;
}

// Distância entre o registro pelo portal e o local de treino, calculada no
// servidor no momento do registro. Existe para o coordenador decidir vendo
// o número, em vez de abrir o mapa e julgar a olho.
//
// Três estados, e a diferença entre eles importa: dentro do raio, fora do
// raio, e "não conferido" — este último quando o núcleo ainda não tem local
// cadastrado ou o registro veio sem GPS. Não conferido não é irregular.
function EtiquetaDistancia({ s }) {
  const base = { fontSize: 10.5, fontWeight: 700, padding: "2px 8px", borderRadius: 20 };

  if (s.dentroDoLocal === true) {
    return (
      <span style={{ ...base, background: "#DCFCE7", color: "#047857" }}
        title={`${s.distanciaMetros} m de ${s.localMaisProximo || "local do núcleo"}`}>
        📍 No local · {s.distanciaMetros} m
      </span>
    );
  }
  if (s.dentroDoLocal === false) {
    const dist = s.distanciaMetros >= 1000
      ? (s.distanciaMetros / 1000).toFixed(1).replace(".", ",") + " km"
      : s.distanciaMetros + " m";
    return (
      <span style={{ ...base, background: "#FFE4E6", color: "#BE123C" }}
        title={`Limite de ${s.raioMetros} m a partir de ${s.localMaisProximo || "local do núcleo"}`}>
        📍 Fora do local · {dist}
      </span>
    );
  }
  return (
    <span style={{ ...base, background: "#F1F5F9", color: "#64748B" }}
      title="O núcleo não tem local de treino cadastrado, ou o registro veio sem localização.">
      📍 Não conferido
    </span>
  );
}

const ESTADO_NO_HISTORICO = {
  aprovada: { txt: "Aprovada", bg: "#D1FAE5", cor: "#047857" },
  negada: { txt: "Negada", bg: PERIGO_CL, cor: "#991B1B" },
  complemento: { txt: "Complemento pedido", bg: "#DBEAFE", cor: "#1E40AF" },
};

export default function SecaoSolicitacoes({
  userNucleo, papel = "coordenacao", modalidadeFixa = null, meuUid = null, hojeISO = null,
}) {
  const isMobile = useIsMobile();
  const ehTecnico = papel === "tecnico";
  const [solicitacoes, setSolicitacoes] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);
  const [resolvendo, setResolvendo] = useState(null); // id em processamento
  const [respostas, setRespostas] = useState({});      // id -> texto de resposta
  const [revertendo, setRevertendo] = useState(null);  // id com o motivo aberto
  const [motivos, setMotivos] = useState({});          // id -> motivo da reversão

  // O que o técnico decidiu e a coordenação ainda não viu.
  //
  // Só para a coordenação: é ela que pode rever, e é dela a ciência.
  const [paraCiencia, setParaCiencia] = useState([]);
  const [dandoCiencia, setDandoCiencia] = useState(null);

  // O histórico é consulta, e consulta só acontece quando alguém pergunta.
  // Eram 95 solicitações vindas a cada abertura da tela, 83 delas já
  // resolvidas em meses fechados, para ninguém olhar.
  const [mesDoHistorico, setMesDoHistorico] = useState(() => new Date().toISOString().slice(0, 7));
  const [historico, setHistorico] = useState(null);     // null = ainda não pedido
  const [buscandoHistorico, setBuscandoHistorico] = useState(false);

  const hoje = hojeISO || new Date().toISOString().slice(0, 10);

  const recarregar = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      // Só a fila. Inteira, de qualquer mês: fila que esconde item deixa de
      // ser fila, e pedido antigo esquecido no fundo é o que não pode
      // acontecer.
      const lista = await carregarSolicitacoesPendentes(userNucleo);
      setSolicitacoes(modalidadeFixa
        ? lista.filter((s) => normalizarModalidade(s.modalidade) === normalizarModalidade(modalidadeFixa))
        : lista);
      setParaCiencia(ehTecnico ? [] : await carregarDecisoesParaCiencia(userNucleo));
    } catch (e) {
      setErro("Erro ao carregar solicitações: " + (e.message || ""));
    } finally {
      setCarregando(false);
    }
  }, [userNucleo, modalidadeFixa, ehTecnico]);

  useEffect(() => { recarregar(); }, [recarregar]);

  async function darCiencia(ids) {
    const lista = Array.isArray(ids) ? ids : [ids];
    setDandoCiencia(lista.length === 1 ? lista[0] : "todas");
    setErro(null);
    try {
      await darCienciaNaDecisao(lista);
      setParaCiencia((atual) => atual.filter((s) => !lista.includes(s.id)));
    } catch (e) {
      setErro("Não foi possível dar ciência: " + (e.message || ""));
    } finally {
      setDandoCiencia(null);
    }
  }

  async function resolver(id, aprovar) {
    setResolvendo(id);
    setErro(null);
    try {
      await resolverSolicitacao({ solicitacaoId: id, aprovar, resposta: respostas[id] || "" });
      await recarregar();
      if (historico !== null) await buscarHistorico();
    } catch (e) {
      setErro("Erro ao resolver: " + (e.message || ""));
    } finally {
      setResolvendo(null);
    }
  }

  // Devolver ao atleta o que faltou, sem negar. A mensagem é obrigatória
  // porque "complemento" sem dizer o quê é pior do que negar: o atleta fica
  // sabendo que precisa refazer e não sabe o que mudar.
  async function pedirComplemento(id) {
    const mensagem = (respostas[id] || "").trim();
    if (mensagem.length < 5) {
      setErro("Escreva no campo de resposta o que o atleta precisa complementar ou corrigir.");
      return;
    }
    setResolvendo(id);
    setErro(null);
    try {
      await solicitarComplemento({ solicitacaoId: id, mensagem });
      await recarregar();
    } catch (e) {
      setErro("Erro ao pedir complemento: " + (e.message || ""));
    } finally {
      setResolvendo(null);
    }
  }

  async function reverter(id) {
    const motivo = (motivos[id] || "").trim();
    if (motivo.length < 5) {
      setErro("Escreva o motivo da reversão.");
      return;
    }
    setResolvendo(id);
    setErro(null);
    try {
      await reverterSolicitacao({ solicitacaoId: id, motivo });
      setRevertendo(null);
      await recarregar();
      if (historico !== null) await buscarHistorico();
    } catch (e) {
      setErro("Erro ao reverter: " + (e.message || ""));
    } finally {
      setResolvendo(null);
    }
  }

  // Busca o histórico do mês escolhido. Recarrega junto com a fila depois
  // de uma decisão, porque o que saiu da fila entrou aqui.
  const buscarHistorico = useCallback(async (mes) => {
    const alvo = mes || mesDoHistorico;
    if (!/^\d{4}-\d{2}$/.test(alvo)) return;
    setBuscandoHistorico(true);
    setErro(null);
    try {
      const [ano, m] = alvo.split("-").map(Number);
      const lista = await carregarSolicitacoesDoMes(userNucleo, m - 1, ano);
      const doMes = lista.filter((s) => s.status !== "pendente");
      setHistorico(modalidadeFixa
        ? doMes.filter((s) => normalizarModalidade(s.modalidade) === normalizarModalidade(modalidadeFixa))
        : doMes);
    } catch (e) {
      setErro("Erro ao carregar o histórico: " + (e.message || ""));
    } finally {
      setBuscandoHistorico(false);
    }
  }, [userNucleo, modalidadeFixa, mesDoHistorico]);

  const fmtData = iso => new Date(iso + "T12:00:00").toLocaleDateString("pt-BR", { weekday: "short", day: "2-digit", month: "2-digit" });

  const pendentes = solicitacoes;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {erro && <StatusBox tipo="erro" msg={erro} />}

      {/* O que o técnico decidiu, esperando a ciência da coordenação.
          Fica aqui em cima porque é a novidade desde a última visita; a
          fila de pendentes, logo abaixo, é o trabalho de sempre. */}
      {!ehTecnico && paraCiencia.length > 0 && (
        <Card>
          <div style={{ display: "flex", gap: 12, alignItems: "baseline", justifyContent: "space-between", flexWrap: "wrap" }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: 16, color: TEXTO }}>
                Decidido pelo técnico ({paraCiencia.length})
              </div>
              <div style={{ fontSize: 12, color: CINZA_ESC, marginTop: 3, lineHeight: 1.55 }}>
                Fica aqui até você dar ciência. Não precisa fazer nada além de ler: para desfazer
                alguma, use Reverter no histórico, que pede o motivo.
              </div>
            </div>
            <Btn variant="secondary" tamanho="sm" disabled={Boolean(dandoCiencia)}
              onClick={() => darCiencia(paraCiencia.map((s) => s.id))}>
              {dandoCiencia === "todas" ? "Marcando…" : "Dar ciência em todas"}
            </Btn>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 14 }}>
            {paraCiencia.map((s) => (
              <div key={s.id} style={{
                border: `1px solid ${BORDA}`, borderRadius: 10, padding: "12px 14px",
                display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap",
              }}>
                <div style={{ flex: 1, minWidth: 220 }}>
                  <div style={{ display: "flex", gap: 8, alignItems: "baseline", flexWrap: "wrap" }}>
                    <span style={{ fontWeight: 700, fontSize: 13.5, color: TEXTO }}>{s.atletaNome}</span>
                    <span style={{ fontSize: 12, color: CINZA_ESC }}>{s.modalidade}</span>
                    <span style={{
                      fontSize: 10.5, fontWeight: 700, padding: "2px 9px", borderRadius: 20,
                      background: s.status === "aprovada" ? OK_CL : RUIM_CL,
                      color: s.status === "aprovada" ? OK : RUIM,
                    }}>
                      {s.status === "aprovada" ? "Abonada" : "Negada"}
                    </span>
                    <span style={{ fontSize: 12, color: CINZA_ESC }}>
                      treino de {fmtData(s.dataTreino)}
                    </span>
                  </div>
                  <div style={{ fontSize: 12.5, color: CINZA_ESC, marginTop: 4, lineHeight: 1.5 }}>
                    por {s.resolvidoPorNome || "técnico"}
                    {s.justificativa ? <> · “{String(s.justificativa).slice(0, 120)}”</> : null}
                  </div>
                </div>
                <Btn variant="discreto" tamanho="sm" disabled={Boolean(dandoCiencia)}
                  onClick={() => darCiencia(s.id)}>
                  {dandoCiencia === s.id ? "…" : "Ciente"}
                </Btn>
              </div>
            ))}
          </div>
        </Card>
      )}

      <Card>
        <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 2 }}>
          {ehTecnico
            ? `Justificativas · ${modalidadeFixa || ""}`
            : "Solicitações pendentes"}
          {pendentes.length > 0 ? ` (${pendentes.length})` : ""}
        </div>
        <div style={{ fontSize: 12, color: CINZA_ESC, marginBottom: 14, lineHeight: 1.55 }}>
          {ehTecnico ? (
            <>
              Você aprova ou nega as justificativas de falta dos atletas da sua modalidade, e pode
              devolver o pedido ao atleta pedindo o que faltou. Exceção a prazo, recurso e pedido
              sobre registro de presença continuam com a coordenação, que também pode rever o que
              você decidir.
            </>
          ) : (
            <>
              Pedidos de correção de falta e registros bloqueados por distância. Um pedido de presença bloqueada
              segura a presença do atleta até você decidir, então vale resolver no mesmo dia.
              Os documentos anexados continuam abertos no histórico, abaixo, depois de resolvidos.
            </>
          )}
        </div>

        {carregando ? (
          <Carregando linhas={4} />
        ) : pendentes.length === 0 ? (
          <div style={{ padding: "14px", background: CINZA, borderRadius: 8, fontSize: 13, color: CINZA_ESC, textAlign: "center" }}>
            Nenhuma solicitação pendente.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {pendentes.map(s => {
              const impedimento = ehTecnico
                ? impedimentoDoTecnico({ solicitacao: s, uid: meuUid, modalidade: modalidadeFixa, hojeISO: hoje })
                : null;
              return (
                <div key={s.id} style={{ border: "1.5px solid #FDE68A", background: "#FFFBEB", borderRadius: 10, padding: 14 }}>
                  <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap", marginBottom: 6 }}>
                    <span style={{ fontWeight: 800, fontSize: 14, color: TEXTO }}>{s.atletaNome}</span>
                    <span style={{ fontSize: 12, color: CINZA_ESC }}>{s.modalidade}</span>
                    {s.origem === "portal_presenca" && <span style={{ fontSize: 10.5, fontWeight: 700, padding: "2px 8px", borderRadius: 20, background: "#DBEAFE", color: "#1E40AF" }}>📱 Registro iOS</span>}
                    {s.origem === "tecnico" && <span style={{ fontSize: 10.5, fontWeight: 700, padding: "2px 8px", borderRadius: 20, background: "#EDE9FE", color: "#6D28D9" }}>Correção do técnico</span>}
                    {s.origem === "fora_do_local" && <span style={{ fontSize: 10.5, fontWeight: 700, padding: "2px 8px", borderRadius: 20, background: "#FFE4E6", color: "#BE123C" }}>📍 Fora do local</span>}
                    {s.origem === "fora_do_raio" && <span style={{ fontSize: 10.5, fontWeight: 700, padding: "2px 8px", borderRadius: 20, background: "#FEE2E2", color: "#991B1B" }}>🚫 Presença bloqueada</span>}
                    {s.origem === "rosto_nao_confere" && <span style={{ fontSize: 10.5, fontWeight: 700, padding: "2px 8px", borderRadius: 20, background: "#EDE9FE", color: "#6D28D9" }}>🙂 Rosto não confere</span>}
                    {(s.origem === "portal_presenca" || s.origem === "fora_do_raio") && <EtiquetaDistancia s={s} />}
                    <span style={{ fontSize: 12, fontWeight: 700, color: "#92400E", marginLeft: "auto" }}>
                      {s.origem === "portal_presenca" || s.origem === "fora_do_local" || s.origem === "fora_do_raio" ? "Presença: " : "Treino: "}{fmtData(s.dataTreino)}
                    </span>
                  </div>
                  <div style={{ fontSize: 13.5, color: TEXTO, background: "#fff", borderRadius: 8, padding: "10px 12px", marginBottom: 10, lineHeight: 1.5 }}>
                    {s.justificativa}
                  </div>
                  <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 10 }}>
                    {s.documentoUrl ? (
                      <a href={s.documentoUrl} target="_blank" rel="noopener noreferrer"
                        style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 700, color: VERDE, textDecoration: "none" }}>
                        {ehFotoDeRegistro(s.origem)
                          ? "📷 Ver foto do registro"
                          : "📎 Ver comprovante anexado"}
                      </a>
                    ) : (
                      <span style={{ fontSize: 12.5, color: CINZA_ESC }}>Sem documento anexado</span>
                    )}
                    {s.locationGoogleMapsLink && (
                      <a href={s.locationGoogleMapsLink} target="_blank" rel="noopener noreferrer"
                        style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 700, color: "#0891B2", textDecoration: "none" }}>
                        📍 Ver localização
                      </a>
                    )}
                  </div>

                  {impedimento ? (
                    <div style={{ fontSize: 12.5, color: "#92400E", background: "#fff", border: "1px solid #FDE68A", borderRadius: 8, padding: "9px 12px", lineHeight: 1.5 }}>
                      {impedimento}
                    </div>
                  ) : (
                    <>
                      <input
                        value={respostas[s.id] || ""}
                        onChange={e => setRespostas(r => ({ ...r, [s.id]: e.target.value }))}
                        placeholder={s.origem === "fora_do_local" ? "Observação sobre o caso (opcional)" : "Resposta ao atleta (opcional, obrigatória para pedir complemento)"}
                        style={{ width: "100%", border: "1px solid #D1D5DB", borderRadius: 10, padding: "8px 11px", fontSize: 13, fontFamily: "inherit", boxSizing: "border-box", marginBottom: 10 }}
                      />
                      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                        <Btn onClick={() => resolver(s.id, true)} disabled={resolvendo === s.id}>
                          {resolvendo === s.id ? "…" : rotuloAprovar(s.origem)}
                        </Btn>
                        <button onClick={() => resolver(s.id, false)} disabled={resolvendo === s.id}
                          style={{ background: "#fff", color: PERIGO, border: `1.5px solid ${PERIGO}`, borderRadius: 9, padding: "9px 16px", fontSize: 13.5, fontWeight: 700, cursor: "pointer" }}>
                          {s.origem === "fora_do_local" ? "✕ Marcar como irregular" : "✕ Negar"}
                        </button>
                        {!ehFotoDeRegistro(s.origem) && (
                          <button onClick={() => pedirComplemento(s.id)} disabled={resolvendo === s.id}
                            style={{ background: "#fff", color: AZUL, border: `1.5px solid ${AZUL}`, borderRadius: 9, padding: "9px 16px", fontSize: 13.5, fontWeight: 700, cursor: "pointer" }}>
                            ↩ Pedir complemento
                          </button>
                        )}
                      </div>
                      {!ehFotoDeRegistro(s.origem) && (
                        <div style={{ fontSize: 11.5, color: CINZA_ESC, marginTop: 8, lineHeight: 1.5 }}>
                          Pedir complemento devolve o pedido ao atleta com a sua mensagem, sem negar:
                          ele corrige e envia de novo.
                        </div>
                      )}
                    </>
                  )}

                  {s.origem === "fora_do_local" && !impedimento && (
                    <div style={{ fontSize: 12, color: CINZA_ESC, marginTop: 8, lineHeight: 1.5 }}>
                      Este é um aviso: a presença já está registrada e continua contando na frequência
                      das duas formas. Sua escolha aqui fica no histórico como parecer da coordenação.
                    </div>
                  )}
                  {s.origem === "fora_do_raio" && !impedimento && (
                    <div style={{ fontSize: 12, color: "#991B1B", marginTop: 8, lineHeight: 1.5 }}>
                      A presença deste atleta <b>não existe</b> enquanto você não autorizar. O registro
                      foi recusado por estar fora do raio permitido, e só passa a contar na frequência
                      se você autorizar aqui.
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Histórico: só o mês pedido, e só quando pedido. */}
      <Card>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: 12 }}>
          <div style={{ fontWeight: 700, fontSize: 15 }}>Histórico</div>
          <input type="month" value={mesDoHistorico}
            onChange={(e) => { setMesDoHistorico(e.target.value); setHistorico(null); }}
            style={{ border: "1px solid #D1D5DB", borderRadius: 10, padding: "6px 10px", fontSize: 13, fontFamily: "inherit" }} />
          <Btn onClick={() => buscarHistorico()} disabled={buscandoHistorico}
            style={{ fontSize: 13, padding: "7px 14px" }}>
            {buscandoHistorico ? "…" : "Ver o mês"}
          </Btn>
          <span style={{ fontSize: 11.5, color: CINZA_ESC, lineHeight: 1.5 }}>
            O que já foi decidido fica guardado por mês, e é carregado quando você pede.
          </span>
        </div>

        {historico === null ? (
          <div style={{ padding: "12px 14px", background: CINZA, borderRadius: 8, fontSize: 13, color: CINZA_ESC, textAlign: "center" }}>
            Escolha o mês e clique em <b>Ver o mês</b>.
          </div>
        ) : historico.length === 0 ? (
          <div style={{ padding: "12px 14px", background: CINZA, borderRadius: 8, fontSize: 13, color: CINZA_ESC, textAlign: "center" }}>
            Nenhuma solicitação decidida neste mês.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {historico.map(s => {
              const st = ESTADO_NO_HISTORICO[s.status] || ESTADO_NO_HISTORICO.negada;
              return (
                <div key={s.id} style={{ padding: "7px 0", borderBottom: "1px solid #F1F5F9" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13, flexWrap: "wrap" }}>
                    <span style={{ fontWeight: 700, color: TEXTO, minWidth: 70 }}>{fmtData(s.dataTreino)}</span>
                    <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 20, background: st.bg, color: st.cor }}>{st.txt}</span>
                    <span style={{ color: TEXTO, fontWeight: 600 }}>{s.atletaNome}</span>
                    <span style={{ color: CINZA_ESC, flex: 1, minWidth: 120 }}>{s.justificativa}</span>
                    {s.documentoUrl && (
                      <a href={s.documentoUrl} target="_blank" rel="noopener noreferrer"
                        style={{ fontSize: 12, fontWeight: 700, color: VERDE, textDecoration: "none", whiteSpace: "nowrap" }}>
                        {ehFotoDeRegistro(s.origem) ? "📷 foto" : "📎 comprovante"}
                      </a>
                    )}
                    {s.resolvidoPorNome && (
                      <span style={{ fontSize: 11.5, color: CINZA_ESC }}>
                        por {s.resolvidoPorNome}
                        {s.resolvidoPorPapel === "tecnico" ? " (técnico)" : ""}
                      </span>
                    )}
                    {/* Reverter é julgar recurso, e o quadro deixa isso com a
                        coordenação. O técnico vê o histórico e não o botão. */}
                    {!ehTecnico && (
                      <button onClick={() => setRevertendo(revertendo === s.id ? null : s.id)}
                        disabled={resolvendo === s.id}
                        style={{ background: "none", border: "none", color: VERDE, fontSize: 12, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}>
                        {resolvendo === s.id ? "…" : "↩ Reverter"}
                      </button>
                    )}
                  </div>

                  {/* O motivo é exigido porque reverter desfaz uma decisão que
                      alguém já comunicou ao atleta. Sem ele, o histórico guarda
                      que a decisão mudou e não guarda por quê. */}
                  {!ehTecnico && revertendo === s.id && (
                    <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: isMobile ? "wrap" : "nowrap" }}>
                      <input
                        value={motivos[s.id] || ""}
                        onChange={e => setMotivos(m => ({ ...m, [s.id]: e.target.value }))}
                        placeholder="Motivo da reversão (fica no histórico)"
                        style={{ flex: 1, minWidth: 200, border: "1px solid #D1D5DB", borderRadius: 10, padding: "7px 10px", fontSize: 13, fontFamily: "inherit" }}
                      />
                      <Btn onClick={() => reverter(s.id)} disabled={resolvendo === s.id}
                        style={{ fontSize: 13, padding: "8px 14px" }}>
                        Confirmar reversão
                      </Btn>
                    </div>
                  )}
                  {s.motivoDaReversao && (
                    <div style={{ fontSize: 11.5, color: CINZA_ESC, marginTop: 4, fontStyle: "italic" }}>
                      Revertida por {s.revertidoPorNome || "coordenação"}: {s.motivoDaReversao}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}
